import { useEffect, useState } from "react";
import { Download, ExternalLink, Loader2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/AppHeader";
import { BillingStrip } from "@/components/BillingWidgets";
import { api, downloadFile, errMsg } from "@/lib/api";

const fmt = (v, cur) => new Intl.NumberFormat("en-US", { style: "currency", currency: (cur || "usd").toUpperCase() }).format(v);

const Row = ({ it }) => {
  const [busy, setBusy] = useState(false);
  const own = it.receipt_url?.startsWith("/");
  const dl = async () => {
    setBusy(true);
    try { await downloadFile(it.receipt_url, `RhinoDraft-receipt-${it.id.slice(-8)}.pdf`); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <tr className="border-b border-[var(--line)]/60" data-testid={`history-row-${it.id}`}>
      <td className="py-3.5 pr-4 font-mono text-xs text-[var(--muted)] whitespace-nowrap">{new Date(it.date).toLocaleDateString()}</td>
      <td className="py-3.5 pr-4 text-sm">{it.description}</td>
      <td className="py-3.5 pr-4 text-right font-mono text-sm whitespace-nowrap">{fmt(it.amount, it.currency)}</td>
      <td className="py-3.5 pr-4"><span className={`font-mono text-[10px] uppercase tracking-widest ${it.status === "paid" ? "text-[var(--emerald)]" : "text-[var(--amber)]"}`}>{it.status}</span></td>
      <td className="py-3.5 text-right whitespace-nowrap">
        {own ? (
          <button data-testid={`receipt-btn-${it.id}`} onClick={dl} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-[var(--cyan)] hover:underline">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Receipt
          </button>
        ) : it.receipt_url ? (
          <a data-testid={`receipt-btn-${it.id}`} href={it.receipt_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[var(--cyan)] hover:underline"><Download size={12} /> Invoice PDF</a>
        ) : <span className="text-xs text-[var(--muted)]">—</span>}
        {it.view_url && <a href={it.view_url} target="_blank" rel="noreferrer" className="ml-3 inline-flex text-[var(--muted)] hover:text-white" title="View on Stripe"><ExternalLink size={12} /></a>}
      </td>
    </tr>
  );
};

export default function BillingHistory() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    document.title = "Billing history · RhinoDraft AI";
    api.get("/billing/history").then(({ data }) => setItems(data.items)).catch((e) => { toast.error(errMsg(e)); setItems([]); });
  }, []);
  const total = (items || []).filter((i) => i.status === "paid").reduce((s, i) => s + i.amount, 0);
  return (
    <div className="min-h-screen blueprint-grid">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-6 py-14">
        <div className="rise flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)]">Billing</p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">Payment history</h1>
          </div>
          <BillingStrip />
        </div>
        <div className="rise d2 mt-10 border border-[var(--line)] bg-[var(--surface)] p-5 sm:p-6 overflow-x-auto" data-testid="billing-history">
          {!items ? <div className="py-10 grid place-items-center"><Loader2 className="animate-spin text-[var(--cyan)]" /></div>
            : items.length === 0 ? (
              <div className="py-12 text-center" data-testid="history-empty"><Receipt className="mx-auto text-[var(--muted)]" /><p className="mt-3 text-sm text-[var(--muted)]">No payments yet.</p></div>
            ) : (
              <table className="w-full min-w-[640px]">
                <thead><tr className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] text-left border-b border-[var(--line)]">
                  <th className="pb-3 font-normal">Date</th><th className="pb-3 font-normal">Description</th><th className="pb-3 font-normal text-right pr-4">Amount</th><th className="pb-3 font-normal">Status</th><th className="pb-3 font-normal text-right">Receipt</th>
                </tr></thead>
                <tbody>{items.map((it) => <Row key={it.id} it={it} />)}</tbody>
                <tfoot><tr><td colSpan={2} className="pt-4 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Total paid</td>
                  <td className="pt-4 pr-4 text-right font-mono text-sm text-[var(--cyan)]" data-testid="history-total">{fmt(total)}</td><td colSpan={2} /></tr></tfoot>
              </table>
            )}
        </div>
      </main>
    </div>
  );
}
